import React from "react";
import { random } from "lodash";
import moment from "moment";

const RequiredStar = ({ required }) => <span className="has-text-danger">{required ? "* " : ""}</span>;

export const FormSelectField = ({
  required,
  className,
  options,
  label,
  value,
  onValueChange,
  defaultEmptyOption = true,
}) => {
  return (
    <div className={`field ${className}`}>
      <label className="label is-small">
        <RequiredStar required={required} />
        {label}
      </label>
      <div className="select is-small is-fullwidth">
        <select
          required={required}
          value={value}
          className="is-radiusless"
          onChange={(e) => onValueChange(e.target.value)}
        >
          {defaultEmptyOption && <option key="" value="" />}
          {options?.map(({ label, value }) => (
            <option key={label} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export const FormMultipleSelectField = ({ selectedValues, required, className, options, label, onValueChange }) => {
  return (
    <div className={`field ${className}`}>
      {label && (
        <label className="label is-small">
          <RequiredStar required={required} />
          {label}
        </label>
      )}
      <div className="select is-small is-fullwidth is-multiple">
        <select
          multiple
          value={[...selectedValues]}
          required={required}
          className="is-radiusless"
          onChange={(e) => onValueChange(Array.from(e.target.selectedOptions))}
        >
          {options?.map(({ label, value }) => (
            // keyed by value: a label carrying a count would otherwise be a new key on
            // every recount, rebuilding every option
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export const FormDateField = ({ required, className, label, value, placeholder, onValueChange }) => (
  <div className={`field ${className}`}>
    <label className="label is-small">
      <RequiredStar required={required} />
      {label}
    </label>
    <div className="control">
      <input
        className="input is-small is-radiusless"
        type="date"
        required={required}
        value={value}
        placeholder={placeholder || label}
        onChange={(e) => onValueChange(e.target.value)}
      />
    </div>
  </div>
);

export const FormWeekToAppointmentField = ({ required, className, label, value, dateValue, onValueChange }) => (
  <div className={`field ${className}`}>
    <label className="label is-small">
      <RequiredStar required={required} />
      {label}
    </label>
    <div className="is-small is-radiusless">
      <input className="week-to-appointment-input" value={value} disabled></input>
      <input
        className="is-small week-to-appointment-calendar"
        type="date"
        value={dateValue ?? ""}
        onChange={(e) => onValueChange(e.target.value)}
      />
    </div>
  </div>
);

export const FormTextField = ({ required, className, label, value, placeholder, onValueChange }) => (
  <div className={`field ${className}`}>
    <label className="label is-small">
      <RequiredStar required={required} />
      {label}
    </label>
    <div className="control">
      <input
        className="input is-small is-radiusless"
        type="text"
        required={required}
        value={value}
        placeholder={placeholder || label}
        onChange={(e) => onValueChange(e.target.value)}
      />
    </div>
  </div>
);

export const CheckboxField = ({ label, checked }) => (
  <label className="field checkbox">
    <input type="checkbox" checked={checked} />
    <span className="is-size-7">{` ${label}`}</span>
  </label>
);

export const DatalistField = ({
  options = [],
  id,
  required,
  className,
  label,
  onValueChange,
  onSelect,
  defaultValue,
  isLoading,
  disabled,
}) => {
  const handleChange = (e) => {
    const value = e.target.value;
    const selectedItem = options.find((option) => option === value);

    if (selectedItem) {
      onSelect(selectedItem);
      return;
    }

    onValueChange(value);
  };

  return (
    <div className={`field ${className}`}>
      <label className="label is-small">
        <RequiredStar required={required} />
        {label}
      </label>
      <div className={`control${isLoading ? " is-loading" : ""}`}>
        <input
          className="input is-small is-radiusless"
          list={id}
          onChange={handleChange}
          defaultValue={defaultValue}
          disabled={disabled}
        />
        <datalist key={random(true)} id={id}>
          {options?.map((value) => (
            <option key={`${moment().unix()}${random(true)}${value}`} value={value} />
          ))}
        </datalist>
      </div>
    </div>
  );
};
